import { createVNode, render } from 'vue';
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import ApplyTemplate from '@/components/widgets/ApplyTemplate.vue';
import { Editor } from '@tiptap/core';
import { ResourceType } from '@/types/resources';

const key = new PluginKey('contentMenu');

type DecorationBuilderProp = {
  state: EditorState;
  resourceType: ResourceType;
  editor: any;
};

let vnode: any = null;
let preventDecorationCreation = false;

export const createContentMenuDecoration = ({
  state,
  resourceType,
  editor,
}: DecorationBuilderProp) => {
  if (preventDecorationCreation) return DecorationSet.empty;
  const decoration = Decoration.widget(state.selection.from, () => {
    const container = document?.createElement('div');
    const menuElement = document?.createElement('div');
    if (!editor.resourceType) return container;
    menuElement.id = 'content-menu';
    container.id = 'content-menu-container';
    container.style.position = 'absolute';
    container.style.top = '50px';
    vnode = createVNode(ApplyTemplate, {
      resourceType,
      enableTooltip: false,
      pageViewTemplate: true,
      key: editor.id,
    });

    if (!editor.appContext) return container;
    vnode.appContext = editor.appContext;
    render(vnode, menuElement);
    container?.appendChild(menuElement);
    document?.body?.appendChild(container);

    return container;
  });

  const decorationSet = DecorationSet.create(state.doc, [decoration]);

  return decorationSet || DecorationSet.empty;
};
export type contentMenuPluginProps = {
  editor: Editor;
  resourceType: ResourceType;
};
const contentMenuPlugin = ({ editor, resourceType }: contentMenuPluginProps) => {
  return new Plugin({
    key,
    state: {
      init() {
        return {
          decoration: DecorationSet.empty,
        };
      },
      apply(tr, stateValue, oldState, newState) {
        const meta = tr.getMeta('contentMenu');
        switch (meta?.type) {
          case 'remove':
            preventDecorationCreation = true;
            return { ...stateValue, decoration: DecorationSet.empty, componentInstance: null };
          case 'preventDecorationCreation':
            preventDecorationCreation = meta.value;
            break;
          default:
            break;
        }
        let emptyEditor = false;
        const { firstChild, childCount } = newState.doc;
        if (firstChild) {
          const { type, content } = firstChild;
          emptyEditor = childCount === 1 && type.name === 'paragraph' && content.size === 0;
        }
        if (!emptyEditor) {
          return { ...stateValue, decoration: DecorationSet.empty };
        }
        preventDecorationCreation = false;
        const [decoration] = stateValue.decoration.find();
        // if decoration is already present, don't remove it and don't create a new one
        if (decoration) {
          return { ...stateValue };
        }

        const newDecoration = createContentMenuDecoration({
          state: newState,
          resourceType,
          editor,
        });
        return { ...stateValue, decoration: newDecoration, componentInstance: vnode?.component };
      },
    },

    view(editorView) {
      const pluginKey = this?.key;
      return {
        destroy() {
          const currentState = pluginKey?.getState(editorView.state);

          if (currentState?.query) {
            currentState.query.viewDestroy();
          }
        },
      };
    },
    props: {
      decorations(state) {
        return this.getState(state)?.decoration;
      },
    },
  });
};

export default contentMenuPlugin;
